// frontend/src/rma/RMANewCaseWizardModal.jsx
//
// Responsive wrapper for the RMA New-Case wizard.
//   Desktop (≥ sm)  → MUI Dialog (modal overlay, max-width md)
//   Mobile  (< sm)  → SwipeableDrawer anchored bottom (bottom-sheet),
//                     matches POG mobile convention (POStatusBar etc.)
//
// Hosts a single instance of RMANewCaseWizardContent in "modal" mode
// (no PageContainer / ViewHeader chrome — the Dialog/Drawer is the chrome).
//
// The wizard's internal `goToCase` will navigate to /rma/<id> after a case
// is created — we wire `onClose` so the modal closes BEFORE the navigation
// fires (so the user lands cleanly on the new case detail).
//
// Per Samuel 2026-05-20.

import React from 'react'
import {
  Dialog, DialogContent, SwipeableDrawer,
  Box, IconButton, useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import RMANewCaseWizardContent from './RMANewCaseWizardContent'
import { BOTTOM_SHEET_TRANSITION } from '../theme/tokens'

export default function RMANewCaseWizardModal({ open, onClose, onCreated }) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // After case creation the inner wizard calls onClose then navigates to
  // the case detail (see RMANewCaseWizardContent.goToCase). onCreated is
  // optional — kept so future callers can hook (analytics, refresh list).
  const handleClose = () => {
    onClose?.()
  }

  const handleAfterCreate = (createdCase) => {
    // Fire any external callback first, then close. The inner wizard
    // navigates to /rma/<id> on its own.
    onCreated?.(createdCase)
    onClose?.()
  }

  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={handleClose}
        onOpen={() => {}}
        disableSwipeToOpen
        // keepMounted=false: unmount when closed so wizard state resets
        // between opens (no stale order pick / form data).
        ModalProps={{ keepMounted: false }}
        PaperProps={{
          sx: {
            height: '92vh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            overflow: 'hidden',
          },
        }}
        {...BOTTOM_SHEET_TRANSITION}
      >
        <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Drag handle — visual cue for swipe-down close */}
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1, pb: 0.5, flexShrink: 0 }}>
            <Box sx={{ width: 36, height: 4, borderRadius: 99, bgcolor: 'divider' }} />
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{ position: 'absolute', top: 6, right: 8, zIndex: 1 }}
            aria-label="Close new case wizard"
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ flex: 1, overflowY: 'auto', pt: 2 }}>
            {open && (
              <RMANewCaseWizardContent
                modalMode
                onClose={handleClose}
                onCreated={handleAfterCreate}
              />
            )}
          </Box>
        </Box>
      </SwipeableDrawer>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      // Fixed dimensions per Samuel 2026-05-20 — narrow but tall, prevents
      // the modal from resizing as user steps through the wizard. Input
      // fields don't need extra width; vertical space is more useful.
      maxWidth={false}
      keepMounted={false}
      PaperProps={{
        sx: {
          borderRadius: 2,
          width: 560,
          // Lower fixed height — fits Step 0 (tile selection) cleanly with
          // breathing room. Longer steps (inquiry step 1 with all fields,
          // return/service with many search results) scroll internally.
          // No resize between steps (which was the original complaint).
          height: 'min(520px, 90vh)',
          maxWidth: '95vw',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogContent sx={{ position: 'relative', p: { xs: 2, sm: 3 }, flex: 1, overflowY: 'auto' }}>
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
          aria-label="Close new case wizard"
        >
          <CloseIcon />
        </IconButton>
        {open && (
          <RMANewCaseWizardContent
            modalMode
            onClose={handleClose}
            onCreated={handleAfterCreate}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
